import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Box } from "@mui/material";
import { AppQuill } from "../../../../Component/Common/Components/FormValidationInputs/AppQuill";
import { WorkforceProfileGoalsAndTargetsService } from "../../../../Component/Api/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargetsService";
import { WorkforceProfileGoalsAndTargets } from "../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargets";
import { EEOccupationalLevel } from "../../../../Component/Model/Enum/EmploymentEquity";
import { workforceCommentSchema } from "./WorkforceCommentValidation";
import { toast } from "react-toastify";
import { EEScorecardAtom } from "../../../../State/EEScorecard.Atom";
import { useRecoilValue } from "recoil";
import { ConsolidationAtom } from "../../../../State/Consolidation.Atom";

interface IProps {
    comment?: WorkforceProfileGoalsAndTargets.CommentsDtoOut[];
    occupationalLevel: EEOccupationalLevel;
    scorecardId: string;
    isDisabled: boolean;
}

type CommentFormData = {
    comment: string;
}

export const WorkforceComments: FC<IProps> = (props) => {
    const [initialComment, setInitialComment] = useState<WorkforceProfileGoalsAndTargets.CommentsDtoOut | null>(null);
    const initialCommentRef = useRef<WorkforceProfileGoalsAndTargets.CommentsDtoOut | null>(null);
    const scorecard = useRecoilValue(EEScorecardAtom);
    const isConsolidatedScorecard = useRecoilValue(ConsolidationAtom);
    const maxConsolidatedCommentLength = 3000 * (scorecard.ConsolidationSourceScorecardsCount ?? 1);
    const maxCommentLength = isConsolidatedScorecard ? maxConsolidatedCommentLength : 3000;

    const { control, reset, getValues } = useForm<CommentFormData>({
        mode: 'all',
        resolver: yupResolver(workforceCommentSchema(maxCommentLength))
    });

    useEffect(() => {
        const matchingComment = props.comment?.find(comment => comment.occupationalLevel === props.occupationalLevel);
        if (matchingComment) {
            setInitialComment(matchingComment);
            initialCommentRef.current = matchingComment;
            reset({ comment: matchingComment.comment });
        } else {
            setInitialComment(null);
            initialCommentRef.current = null;
            reset({ comment: '' });
        }
    }, [props.comment, props.occupationalLevel, reset]);

    useEffect(() => {
        return () => {
            if (localStorage.getItem('coreToken') !== null) {
                saveCommentForm();
            }
        };
    }, [getValues]);

    const saveCommentForm = async () => {
        const formData = getValues();
        if (formData.comment.length > 0) {
            workforceCommentSchema(maxCommentLength).validate(formData, { abortEarly: false })
                .then(async () => {
                    const requestData = {
                        commentId: initialCommentRef.current?.commentId,
                        comment: formData.comment,
                        isDisabled: props.isDisabled,
                        occupationalLevel: props.occupationalLevel,
                        scorecardId: props.scorecardId,
                    };

                    await WorkforceProfileGoalsAndTargetsService.SaveComment(requestData);
                })
                .catch(function (error: any) {
                    toast.error(error.message);
                });
        }
    };

    return (
        <Box mt={2}>
            <AppQuill control={control} name='comment' label='Comments for EEA12 Report' maxLength={maxCommentLength} />
        </Box>
    );
};
